import React, { useState, useEffect } from "react";
import axios from "axios";
import { CSVLink } from "react-csv";
import PageTitle from "../../components/common/PageTitle";
import { Container, Row, Card, Button } from "shards-react";

const staticHeaders = [
  { key: "studentId", label: "Student ID" },
  { key: "first_name", label: "First name" },
  { key: "middle_name", label: "Middle name" },
  { key: "last_name", label: "Last name" },
  { key: "email_id", label: "Email" },
  { key: "phone", label: "Phone Number" },
  { key: "country", label: "Country" },
  { key: "state", label: "State" },
  { key: "district", label: "District" },
  { key: "city", label: "City" },
  { key: "gender", label: "Gender" },
  { key: "school", label: "Institution" },
  { key: "school_type", label: "Institution type" },
  { key: "class", label: "Class" },
  { key: "haveAtlLab", label: "School has ATL lab" },
  { key: "atlCode", label: "ATL Code" },
  { key: "enrolled_at", label: "Enrollment Date" },
];

const projectHeaders = [
  { key: "theme", label: "Theme" },
  { key: "sdg", label: "SDG" },
  { key: "projectType", label: "Project type" },
  { key: "members", label: "Team members" },
  { key: "ethicalConcern", label: "Ethical concern" },
  { key: "source", label: "Source" },
  { key: "pdf", label: "Project PDF" },
  { key: "video", label: "Video" },
  { key: "videoURL", label: "Video URL" },
  { key: "hasDeployedInnovation", label: "Deployed innovation" },
  { key: "journeyDocumentURL", label: "Journey document link" },
  { key: "submissionStatus", label: "Submission status" },
  { key: "submittedAt", label: "Submitted at" },
];

// react-csv (v2) wraps every field in double quotes but does NOT escape quotes
// embedded in a value, so any cell containing a `"` (e.g. a student's free-text
// "ethical concern") terminates the field early and shifts every later column
// right — which is how a video URL ends up under a later heading. Double the
// internal quotes per RFC 4180 so the library's wrapping stays valid. Commas and
// newlines need no handling here: every field is already wrapped.
const escapeCsvValues = (row) => {
  const escaped = {};
  Object.keys(row).forEach((key) => {
    const value = row[key];
    escaped[key] =
      typeof value === "string" ? value.replace(/"/g, '""') : value;
  });
  return escaped;
};

const CollapsibleRow = ({ year }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [listLoaded, setListLoaded] = useState(false);

  const fetchData = async (year) => {
    setListLoaded(false);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/atl-tinkerpreneur/participants?year=${year}`,
        {
          headers: { "x-access-token": localStorage.getItem("jwtToken") },
        }
      );
      const participants = (response.data.data || []).map(escapeCsvValues);
      setData(participants);
      if (participants.length > 0) {
        const first = participants[0];
        const dynamicSessionHeaders = Object.keys(first)
          .filter((key) => key.startsWith("dynmc_"))
          .map((key) => ({
            key,
            label: key.replace(/^dynmc_/, ""),
          }));

        setCsvHeaders([...staticHeaders, ...dynamicSessionHeaders, ...projectHeaders]);
      } else {
        setCsvHeaders([...staticHeaders, ...projectHeaders]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setListLoaded(true);
    }
  };

  useEffect(() => {
    if (open) {
      fetchData(year);
    }
  }, [open, year]);

  return (
    <Card
      onClick={() => setOpen(!open)}
      className="p-3 shadow-sm my-3 rounded-lg"
    >
      <div className="d-flex justify-content-between align-items-center">
        <h5 className="m-0 font-weight-bold text-dark">
          AI Tinkerpreneur {year}
        </h5>
        <span
          className={`font-weight-bold ${
            open ? "text-danger" : "text-primary"
          }`}
        >
          {open ? "▲" : "▼"}
        </span>
      </div>

      {open && (
        <div className="mt-3">
          {listLoaded ? (
            <CSVLink
              data={data}
              headers={csvHeaders}
              filename={`AI-Tinkerpreneur-${year}.csv`}
            >
              <Button size="sm" theme="primary">
                Download CSV
              </Button>
            </CSVLink>
          ) : (
            <Button disabled>Loading...</Button>
          )}
        </div>
      )}
    </Card>
  );
};

const TinkerpreneurDashboard = () => {
  const years = [2026, 2025];

  return (
    <Container fluid className="main-content-container px-4">
      <Row noGutters className="page-header py-4">
        <PageTitle
          sm="4"
          title="AI Tinkerpreneur List"
          subtitle=""
          className="text-sm-left"
        />
      </Row>
      {years.map((year) => (
        <CollapsibleRow key={year} year={year} />
      ))}
    </Container>
  );
};

export default TinkerpreneurDashboard;
